import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Flower2, Mail, Truck, CheckCircle2, XCircle, Eye, Archive,
  RefreshCw, ImageIcon, Loader2, Send, Globe2,
} from "lucide-react";
import api from "../../console/api";

const STATUS_OPTIONS = [
  { value: "submitted",    label: "New",          tone: "bg-[#5C1A1B]/10 text-[#5C1A1B]" },
  { value: "approved",     label: "Approved",     tone: "bg-emerald-100 text-emerald-800" },
  { value: "declined",     label: "Declined",     tone: "bg-stone-200 text-stone-700" },
  { value: "courier_sent", label: "Courier sent", tone: "bg-amber-100 text-amber-900" },
  { value: "received",     label: "Received",     tone: "bg-sky-100 text-sky-900" },
  { value: "published",    label: "Published",    tone: "bg-violet-100 text-violet-900" },
  { value: "archived",     label: "Archived",     tone: "bg-stone-100 text-stone-500" },
];

const STATUS_LOOKUP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

export default function AdminSeedlings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/seedlings");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Couldn't load seedling submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  const counts = (() => {
    const out = { all: rows.length };
    for (const s of STATUS_OPTIONS) out[s.value] = rows.filter((r) => r.status === s.value).length;
    return out;
  })();

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
      <header className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#2A241F] flex items-center gap-3">
            <Flower2 className="w-7 h-7 text-[#5C1A1B]" />
            Heritage Garden, Breeders&rsquo; Bed
          </h1>
          <p className="mt-1.5 text-sm text-[#2A241F]/65 font-display italic">
            Seedling submissions from breeders, review, accept, send the courier ticket, then publish to the public garden page.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="seedlings-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#5C1A1B]/20 text-[#5C1A1B] hover:bg-[#5C1A1B]/8 text-sm"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#5C1A1B]/10 pb-4">
        <FilterChip current={filter} setFilter={setFilter} v="all" label="All" count={counts.all} />
        {STATUS_OPTIONS.map((s) => (
          <FilterChip key={s.value} current={filter} setFilter={setFilter} v={s.value} label={s.label} count={counts[s.value] || 0} />
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#2A241F]/60 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-[#2A241F]/60 italic font-display">
          {filter === "all" ? "No submissions yet." : `No submissions with status '${STATUS_LOOKUP[filter]?.label || filter}'.`}
        </div>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((r) => (
            <li key={r.id} className="border border-[#5C1A1B]/15 rounded-lg overflow-hidden bg-white shadow-sm">
              {r.photos?.[0] && (
                <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                  <img src={r.photos[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-xl text-[#2A241F]">{r.dahlia_name}</h3>
                    <p className="text-xs text-[#2A241F]/60 mt-0.5">
                      {r.breeder_name}{r.breeder_region ? ` · ${r.breeder_region}` : ""}
                    </p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <p className="mt-3 text-sm text-[#2A241F]/80 line-clamp-3">{r.description}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActive(r)}
                    data-testid={`seedlings-open-${r.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#5C1A1B] hover:bg-[#7B2B2E] text-white text-xs font-label tracking-[0.18em] uppercase"
                  >
                    <Eye className="w-3 h-3" /> Open
                  </button>
                  {r.credit_nzd != null && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#5C1A1B] font-semibold">
                      ${Number(r.credit_nzd).toFixed(2)} credit
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {active && (
        <SubmissionModal
          row={active}
          onClose={() => setActive(null)}
          onSaved={(updated) => {
            setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
            setActive(updated);
          }}
          onDeleted={(id) => {
            setRows((rs) => rs.filter((r) => r.id !== id));
            setActive(null);
          }}
        />
      )}
    </div>
  );
}

function FilterChip({ current, setFilter, v, label, count }) {
  const on = current === v;
  return (
    <button
      type="button"
      onClick={() => setFilter(v)}
      data-testid={`seedlings-filter-${v}`}
      className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-label tracking-[0.18em] uppercase border ${
        on ? "bg-[#5C1A1B] text-white border-[#5C1A1B]" : "bg-white text-[#2A241F]/70 border-stone-200 hover:border-[#5C1A1B]/40"
      }`}
    >
      {label} <span className={`px-1.5 rounded text-[10px] ${on ? "bg-white/20" : "bg-stone-100"}`}>{count}</span>
    </button>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_LOOKUP[status];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-label ${cfg.tone}`}>
      {cfg.label}
    </span>
  );
}

function SubmissionModal({ row, onClose, onSaved, onDeleted }) {
  const [draft, setDraft] = useState({
    status: row.status,
    credit_nzd: row.credit_nzd ?? "",
    courier_ticket: row.courier_ticket || "",
    courier_ticket_url: row.courier_ticket_url || "",
    internal_note: row.internal_note || "",
    decline_reason: row.decline_reason || "",
  });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        status: draft.status,
        credit_nzd: draft.credit_nzd === "" ? null : Number(draft.credit_nzd),
        courier_ticket: draft.courier_ticket.trim(),
        courier_ticket_url: draft.courier_ticket_url.trim(),
        internal_note: draft.internal_note.trim(),
        decline_reason: draft.decline_reason.trim(),
      };
      const r = await api.patch(`/admin/seedlings/${row.id}`, payload);
      toast.success("Saved.");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const resendEmail = async () => {
    try {
      setResending(true);
      await api.post(`/admin/seedlings/${row.id}/email`);
      toast.success("Email re-sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send email.");
    } finally {
      setResending(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete submission '${row.dahlia_name}'? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/seedlings/${row.id}`);
      toast.success("Deleted.");
      onDeleted(row.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-stone-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl text-[#2A241F]">{row.dahlia_name}</h2>
            <p className="text-xs text-[#2A241F]/60 mt-0.5">
              {row.breeder_name} · {row.breeder_email}
              {row.breeder_phone ? ` · ${row.breeder_phone}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700 text-xl" aria-label="Close">×</button>
        </header>

        <div className="px-5 py-5 space-y-5">
          {/* Photos */}
          {row.photos?.length > 0 && (
            <div>
              <Heading icon={<ImageIcon className="w-4 h-4" />}>Photos</Heading>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {row.photos.map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden rounded-md border border-stone-200 bg-stone-50">
                    <img src={p} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Breeder details */}
          <div>
            <Heading>Breeder details</Heading>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Row k="Region" v={row.breeder_region || ","} />
              <Row k="Postal" v={row.breeder_postal_address || ","} />
              <Row k="Bio" v={row.breeder_bio || ","} wide />
            </dl>
          </div>

          {/* Seedling details */}
          <div>
            <Heading>Seedling</Heading>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Row k="Parentage" v={row.parentage || ","} />
              <Row k="Submitted" v={row.created_at ? new Date(row.created_at).toLocaleString("en-NZ") : ","} />
              <Row k="Description" v={row.description || ","} wide />
            </dl>
          </div>

          {/* Decision panel */}
          <div className="border-t border-stone-200 pt-5">
            <Heading>Decision</Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Status</FieldLabel>
                <select
                  data-testid="seedlings-status"
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                  className="mt-1 h-10 w-full px-3 rounded-md border border-stone-300 text-sm"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Credit (NZ$)</FieldLabel>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="seedlings-credit"
                  value={draft.credit_nzd}
                  onChange={(e) => setDraft({ ...draft, credit_nzd: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 h-10 w-full px-3 rounded-md border border-stone-300 text-sm"
                />
              </div>
              <div>
                <FieldLabel>NZ Post tracking number</FieldLabel>
                <input
                  data-testid="seedlings-tracking"
                  value={draft.courier_ticket}
                  onChange={(e) => setDraft({ ...draft, courier_ticket: e.target.value })}
                  placeholder="e.g. ABC123456789"
                  className="mt-1 h-10 w-full px-3 rounded-md border border-stone-300 text-sm font-mono"
                />
              </div>
              <div>
                <FieldLabel>NZ Post ticket PDF / URL</FieldLabel>
                <input
                  data-testid="seedlings-ticket-url"
                  value={draft.courier_ticket_url}
                  onChange={(e) => setDraft({ ...draft, courier_ticket_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1 h-10 w-full px-3 rounded-md border border-stone-300 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Decline reason (only shown to breeder if status = declined)</FieldLabel>
                <textarea
                  data-testid="seedlings-decline-reason"
                  value={draft.decline_reason}
                  onChange={(e) => setDraft({ ...draft, decline_reason: e.target.value })}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-stone-300 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <FieldLabel>Internal note (admin only, never emailed)</FieldLabel>
                <textarea
                  data-testid="seedlings-note"
                  value={draft.internal_note}
                  onChange={(e) => setDraft({ ...draft, internal_note: e.target.value })}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-stone-300 text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <footer className="sticky bottom-0 bg-stone-50 border-t border-stone-200 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={remove}
            data-testid="seedlings-delete"
            className="text-xs text-red-700 hover:text-red-900 underline"
          >
            Delete
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resendEmail}
              disabled={resending}
              data-testid="seedlings-resend-email"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-stone-300 hover:bg-white text-xs font-label tracking-[0.18em] uppercase"
            >
              <Send className="w-3 h-3" /> {resending ? "Sending…" : "Email breeder"}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="seedlings-save"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#5C1A1B] hover:bg-[#7B2B2E] text-white text-xs font-label tracking-[0.18em] uppercase"
            >
              <CheckCircle2 className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Heading({ children, icon }) {
  return (
    <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.32em] uppercase text-[#5C1A1B] font-label">
      {icon}
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="text-[10px] tracking-[0.28em] uppercase text-[#2A241F]/60 font-label">{children}</label>;
}

function Row({ k, v, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <dt className="text-[10px] tracking-[0.24em] uppercase text-[#2A241F]/50 font-label">{k}</dt>
      <dd className="mt-0.5 text-[#2A241F]/90 whitespace-pre-wrap">{v}</dd>
    </div>
  );
}
